import { PageSection, TextContent, Text, PageSectionVariants, Alert, Divider, EmptyState, Title, EmptyStateBody } from '@patternfly/react-core';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchDaemonConfigs, StoreState } from '../../store/reducers';


import './daemon-config-page.css';
import { useTranslation } from 'react-i18next';
import { getCurrentAppClient } from '../../store/selectors';
import DaemonConfigItem from './DaemonConfigItem';


const DaemonConfigPage = () => {

    const appClient = useSelector((state: StoreState) => state.currentAppClient);
    const appConfig = useSelector((state: StoreState) => state.appConfig);
    const currentAppClient = useSelector(getCurrentAppClient);

    // reload daemon configs whenever the page is reloaded, re-opened, or client is changed
    const dispatch = useDispatch();
    const { t } = useTranslation();
    useEffect(() => {
        dispatch(fetchDaemonConfigs(appClient));
    }, [dispatch, appClient]);

    const daemonConfigs = useSelector((state: StoreState) => state.daemonConfigs);
    const daemonConfigsError = useSelector((state: StoreState) => state.daemonConfigsError);

    return (
        <>
            <PageSection variant={PageSectionVariants.default}>
                <TextContent>
                    <Text component="h1" data-test='title' data-testid='daemon-config'>
                        {t('page.daemonConfig.title')}
                    </Text>
                </TextContent>
            </PageSection>

            <PageSection variant={PageSectionVariants.light}>

                {appConfig && appConfig.showErrorsInUi && daemonConfigsError !== null && (
                    <PageSection data-test='daemon-error'>
                        <Alert variant="danger" isInline title="An error occurred when trying to retrieve daemon configuration">
                            <p>An error occurred when trying to retrieve daemon configuration:</p>
                            <p>{daemonConfigsError}</p>
                        </Alert>
                    </PageSection>
                )}

                {!currentAppClient && (
                    <>
                        <EmptyState data-test='daemon-empty' data-testid='no-target'>
                            <Title headingLevel="h4" size="lg">{t('daemonConfigPage.noConfigurationTarget.title')}</Title>
                            <EmptyStateBody>{t('daemonConfigPage.noConfigurationTarget.body')}</EmptyStateBody>
                        </EmptyState>
                    </>
                )}

                {currentAppClient && daemonConfigsError === null && daemonConfigs !== null && daemonConfigs.length === 0 && (
                    <>
                        <EmptyState data-test='daemon-empty' data-testid='no-daemons'>
                            <Title headingLevel="h4" size="lg">{t('daemonConfigPage.noDaemonConfig.title')}</Title>
                            <EmptyStateBody>{t('daemonConfigPage.noDaemonConfig.body')}</EmptyStateBody>
                        </EmptyState>
                    </>
                )}

                {currentAppClient && daemonConfigsError === null && daemonConfigs !== null && daemonConfigs.length > 0 && (
                    <>
                        <PageSection variant={PageSectionVariants.light} className="limit-width">

                            {/* Show a help message when 2+ daemon configs are available */}
                            {daemonConfigs.length > 1 && (
                                <TextContent className="content-header" data-test='daemons-header' data-testid='count'>
                                    <Text component="p">{t('daemonConfigPage.configCountAvailable', { count: daemonConfigs.length })}</Text>
                                </TextContent>)}

                            {daemonConfigs.map((d, index) => (
                                <div className="daemon-config-block" key={d.sessionId} data-test='daemon-config' data-testid={d.sessionId}>
                                    <DaemonConfigItem daemonConfig={d} runningNumber={daemonConfigs.length > 1 ? index + 1 : undefined} />
                                    {index !== daemonConfigs.length - 1 && (<Divider className="content-divider" />)}
                                </div>
                            ))}
                        </PageSection>

                    </>
                )}

            </PageSection>


        </>
    );
}

export default DaemonConfigPage;
